import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { AdminGoalBoard } from '../lib/admin';
import type { GoalFilterKey } from '../lib/goal-view';

// The monitor's headline band — the existing `.metric-grid--5` card tiles, so it
// reads as the platform radar's sibling rather than a bespoke widget.
//
// `waitingOnYou` LEADS, ahead of the percentage. Measured 2026-08-17: 91%
// complete and `in progress: 0` — every unfinished goal was blocked on a human
// or stalled. The bar showed 91% and looked healthy. The first tile answers
// "what do I do next"; the second answers "how far along are we", and only one
// of those changes anyone's day.
//
// Four of the five tiles are also the board's FILTERS, so the number and the
// rows behind it are one control rather than two vocabularies for one set of
// ideas. `overall` is not among them: a percentage is a measure over the whole
// board, not a subset of it, and there is no list of goals it could reveal.

// ponytail: the copy's locale (`t.locale`); a `lib/time.ts` number seam would retire it.
const NUM = new Intl.NumberFormat(t.locale);

type Tile = {
  label: string;
  value: string;
  lead?: boolean;
  /** The narrow this tile applies; absent on a tile that names no subset. */
  filter?: GoalFilterKey;
};

export function GoalSummaryBand(props: {
  board: AdminGoalBoard;
  active: GoalFilterKey | null;
  onFilter: (next: GoalFilterKey | null) => void;
}): JSX.Element {
  const tiles = (): Tile[] => {
    const s = props.board.summary;
    return [
      {
        label: t('goals.waiting_on_you'),
        value: NUM.format(s.waitingOnYou),
        lead: true,
        filter: 'waiting',
      },
      { label: t('goals.overall'), value: `${s.percent}%` },
      {
        label: t('goals.status.in_progress'),
        value: NUM.format(s.inProgress),
        filter: 'in_progress',
      },
      {
        label: t('goals.status.finished'),
        value: `${NUM.format(s.finished)}/${NUM.format(s.goals)}`,
        filter: 'finished',
      },
      { label: t('goals.open_items'), value: NUM.format(s.openItems), filter: 'open' },
    ];
  };

  return (
    <ul class="metric-grid metric-grid--5" aria-label={t('goals.heading')}>
      <For each={tiles()}>
        {(tile) => (
          // The FILTERABLE tiles carry their own class so the whole card can
          // answer the pointer, not just the button inside it. Four of five
          // being interactive is a real difference and the band should show it.
          <li
            class={`card${tile.lead ? ' goal-tile--lead' : ''}${
              tile.filter === undefined ? '' : ' goal-tile--interactive'
            }`}
          >
            {tile.filter === undefined ? (
              <div class="card__content">
                <p class="metric-grid__label">{tile.label}</p>
                <p class="metric-grid__value">{tile.value}</p>
              </div>
            ) : (
              // A real <button>, not a clickable card: this changes what the page
              // shows, so it has to be reachable by keyboard and announce its own
              // state. `aria-pressed` because the tile TOGGLES — pressing the
              // active one clears the narrow, which is the only way back to the
              // whole board without hunting for a separate "all" control.
              <button
                type="button"
                class="card__content goal-tile__filter"
                aria-pressed={props.active === tile.filter}
                onClick={() =>
                  props.onFilter(props.active === tile.filter ? null : (tile.filter ?? null))
                }
              >
                <p class="metric-grid__label">{tile.label}</p>
                <p class="metric-grid__value">{tile.value}</p>
              </button>
            )}
          </li>
        )}
      </For>
    </ul>
  );
}
